import { Typography, TypographyProps } from '@mui/material';
import { FunctionComponent, ReactNode } from 'react';

interface TranslationWithHtmlProps extends TypographyProps {
  translation: string;
  rawText?: boolean;
  replacements?: Record<string, ReactNode>;
}

/**
 * Displays a translation string containing HTML by rendering a Typography component with a span tag as a child.
 * The span tag uses dangerouslySetInnerHtml to render the HTML contained within the translation string.
 * Any valid Typography props will be passed to the underlying Typography component.
 *
 * @component
 * @example
 * <TranslationWithHtml translation={translator.exampleTranslation()} />
 *
 * @param {string} translation - The string containing HTML to display.
 * @param {boolean} rawText - Display the HTML as raw text instead of using the Typography component.
 * @returns {ReactNode} ReactNode representing the TranslationWithHtml.
 */

export const TranslationWithHtml: FunctionComponent<TranslationWithHtmlProps> = ({
  translation,
  rawText,
  replacements,
  ...props
}) => {
  const renderWithReplacements = () => {
    if (!replacements) return null;
    const parts: ReactNode[] = [];
    const regex = /\{(\w+)\}/g;
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = regex.exec(translation)) !== null) {
      if (match.index > lastIndex) {
        parts.push(translation.slice(lastIndex, match.index));
      }
      const key = match[1];
      parts.push(replacements[key] ?? match[0]);
      lastIndex = match.index + match[0].length;
    }
    if (lastIndex < translation.length) {
      parts.push(translation.slice(lastIndex));
    }
    return parts;
  };

  const replacedContent = renderWithReplacements();
  if (replacedContent) {
    return rawText ? <>{replacedContent}</> : <Typography {...props}>{replacedContent}</Typography>;
  }

  const span = (
    <span
      dangerouslySetInnerHTML={{
        __html: translation
      }}
    />
  );

  return rawText ? span : <Typography {...props}>{span}</Typography>;
};
